import React, { useState, useRef, useMemo, useEffect, useCallback } from "react";
import * as XLSX from "xlsx";
import { createPortal } from "react-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import Layout from "../../components/Layout/Layout";
import UserLayout from "../../components/Layout/UserLayout";
import { useAuth } from "../../context/AuthContext";
import LeadsTabs from "./LeadsTabs";
import LeadsKanban from "./LeadsKanban";
import ImportPreview from "./ImportPreview";
import ExportLeadsModal from "./ExportLeadsModal";
import {
  Card,
  Button,
  Badge,
  SearchInput,
  Select,
  SearchableSelect,
  Modal,
  Spinner,
  EmptyState,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Input,
  Tabs,
  Tooltip,
  ConfirmDialog,
  stickyActionCellClass,
  stickyActionHeaderClass,
} from "../../components/UI";
import { formatLabel } from "../../utils/formatLabel";
import { leadsAPI, usersAPI, pipelineAPI, productsAPI, followUpsAPI, emailTemplatesAPI } from "../../services/api";
import {
  validateInternationalPhone,
  validateOptionalInternationalPhone,
  PHONE_COUNTRY_OPTIONS,
  getSavedPhoneCountry,
  savePhoneCountry,
} from "../../utils/validation";
import toast from "react-hot-toast";
import {
  HiOutlinePlus,
  HiOutlineFilter,
  HiOutlineDownload,
  HiOutlineUpload,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineUserAdd,
  HiOutlinePhone,
  HiOutlineMail,
  HiOutlineDotsVertical,
  HiOutlineDocumentText,
  HiOutlineUsers,
  HiOutlineClipboardCheck,
  HiOutlineExclamation,
  HiOutlineCube,
  HiOutlineChevronUp,
  HiOutlineChevronDown,
  HiOutlineCalendar,
  HiOutlineTag,
  HiOutlineChatAlt,
  HiOutlineViewBoards,
  HiOutlineViewList,
} from "react-icons/hi";
import { format } from "date-fns";
import {
  QuickStageModal,
  QuickStatusModal,
  QuickNoteModal,
  QuickFollowUpModal,
  QuickSendEmailModal,
} from "./QuickActionModals";

// Uniform 32x32 icon button used in the Leads table Actions column
const leadActionBtnClass =
  "inline-flex items-center justify-center w-8 h-8 shrink-0 rounded-lg text-secondary-600 hover:bg-secondary-100 hover:text-secondary-800 focus:outline-none focus:ring-2 focus:ring-primary-500/40 transition-colors";

// Name validation function
const validateName = (name) => {
  if (!name) return { isValid: false, error: "Name is required" };

  const cleanName = name.trim();

  if (cleanName.length < 2) {
    return { isValid: false, error: "Name must be at least 2 characters" };
  }

  // Name is allowed to contain letters, numbers, spaces, emojis, and
  // special characters (e.g. "John D.", "O'Brien", "Raj 🌟", "Raj2").
  return { isValid: true, cleanName };
};

// Email validation function
const validateEmail = (email) => {
  if (!email) return { isValid: true, cleanEmail: "" }; // Email is optional

  const cleanEmail = email.trim();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
    return { isValid: false, error: "Invalid email format" };
  }

  return { isValid: true, cleanEmail };
};

// Pipeline stage validation — matches the org's default pipeline stage KEYS
// (see telecallsaas_backend/models/PipelineStage.js getDefaultStages()).
// Custom orgs may have their own stages too, so an unrecognized value is
// still accepted (as a free-form key) rather than rejected outright.
const validateStage = (stage) => {
  if (!stage) return { isValid: true, cleanStage: "new_lead" };

  // Normalize the stage: trim, lowercase, and handle underscores/spaces
  const normalizedInput = stage
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, "_");

  // Valid stage keys (default stages from pipeline)
  const validStages = [
    "new_lead",
    "contact_attempted",
    "not_connected",
    "connected",
    "qualified",
    "follow_up_required",
    "meeting_scheduled",
    "proposal_sent",
    "negotiation",
    "converted",
    "lost",
    "not_interested",
  ];

  // Map common variations to stage keys
  const stageMap = {
    new: "new_lead",
    "new_lead": "new_lead",
    newlead: "new_lead",
    "contact_attempted": "contact_attempted",
    contactattempted: "contact_attempted",
    "not_connected": "not_connected",
    notconnected: "not_connected",
    connected: "connected",
    qualified: "qualified",
    "follow_up": "follow_up_required",
    followup: "follow_up_required",
    "follow_up_required": "follow_up_required",
    "meeting_scheduled": "meeting_scheduled",
    meetingscheduled: "meeting_scheduled",
    "proposal_sent": "proposal_sent",
    proposalsent: "proposal_sent",
    negotiation: "negotiation",
    converted: "converted",
    lost: "lost",
    "not_interested": "not_interested",
    notinterested: "not_interested",
  };

  const normalizedStage = stageMap[normalizedInput] || normalizedInput;

  if (
    !validStages.includes(normalizedStage) &&
    !normalizedStage.includes("_")
  ) {
    // If it doesn't match known stages, still allow it (custom stages)
    return { isValid: true, cleanStage: normalizedInput };
  }

  return { isValid: true, cleanStage: normalizedStage };
};

// Lead disposition status — a separate concept from pipeline stage (see
// telecallsaas_backend/server.js leadSchema.status enum). Optional; defaults
// to "new" and never hard-fails the row (unrecognized values just fall back).
const LEAD_STATUS_VALUES = [
  "new", "open", "in_progress", "follow_up", "cold", "warm", "hot",
  "converted", "closed", "lost", "not_connected",
];
const validateStatus = (status) => {
  if (!status) return { isValid: true, cleanStatus: "new" };
  const normalized = status.trim().toLowerCase().replace(/[\s-]+/g, "_");
  if (!LEAD_STATUS_VALUES.includes(normalized)) {
    return { isValid: true, cleanStatus: "new", warning: `Unrecognized status "${status}" — defaulted to "new"` };
  }
  return { isValid: true, cleanStatus: normalized };
};

// Lead priority — matches the Auto-Assign Rules "Lead Priority" options.
const LEAD_PRIORITY_VALUES = ["urgent", "high", "medium", "low"];
const validatePriority = (priority) => {
  if (!priority) return { isValid: true, cleanPriority: "medium" };
  const normalized = priority.trim().toLowerCase();
  if (!LEAD_PRIORITY_VALUES.includes(normalized)) {
    return { isValid: true, cleanPriority: "medium", warning: `Unrecognized priority "${priority}" — defaulted to "medium"` };
  }
  return { isValid: true, cleanPriority: normalized };
};

const statusOptions = [
  // { value: "new", label: "New" },
  // { value: "open", label: "Open" },
  // { value: "in_progress", label: "In Progress" },
  // { value: "follow_up", label: "Follow Up" },
  { value: "cold", label: "Cold" },
  { value: "warm", label: "Warm" },
  { value: "hot", label: "Hot" },
  // { value: "converted", label: "Converted" },
];

// Build a compact list of page numbers + "..." ellipses for pagination.
// Always shows first and last page, current page ±1, with ellipses for gaps.
// e.g. page 5 of 20 -> [1, "...", 4, 5, 6, "...", 20]
const buildPageList = (current, total) => {
  const pages = [];
  const set = new Set([1, total, current, current - 1, current + 1]);
  for (const p of set) {
    if (p >= 1 && p <= total) pages.push(p);
  }
  pages.sort((a, b) => a - b);

  const result = [];
  for (let i = 0; i < pages.length; i++) {
    result.push(pages[i]);
    if (i < pages.length - 1 && pages[i + 1] - pages[i] > 1) {
      result.push("...");
    }
  }
  return result;
};

const LeadsList = () => {
  const navigate = useNavigate();
  // Telecallers get the same page as "My Leads": only their assigned leads
  // (the backend scopes the queries), without admin-only actions such as
  // assign, bulk actions, import, export and the assignment/people filters.
  const { isTelecaller } = useAuth();
  const PageLayout = isTelecaller ? UserLayout : Layout;
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const fileInputRef = useRef(null);

  // Read initial filters from URL params (for deep-linking from dashboard alerts)
  const initialStatus = searchParams.get("status") || "";
  const initialStage = searchParams.get("stage") || "";
  const initialAssignment = searchParams.get("assignment") || "";

  // State
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [assignmentFilter, setAssignmentFilter] = useState(initialAssignment);
  const [personFilter, setPersonFilter] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showSingleAssignModal, setShowSingleAssignModal] = useState(false);
  const [selectedLeadForAssign, setSelectedLeadForAssign] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showBulkSelectModal, setShowBulkSelectModal] = useState(false);
  const [showBulkStageModal, setShowBulkStageModal] = useState(false);
  const [showBulkStatusModal, setShowBulkStatusModal] = useState(false);
  const [bulkSelectCount, setBulkSelectCount] = useState(50);
  const [selectionMode, setSelectionMode] = useState("page"); // 'page' or 'count'
  const [activeTab, setActiveTab] = useState(initialStage || "all");
  const [importData, setImportData] = useState([]);
  // Off by default so imports follow the org's Auto-Assign Rules (matching
  // how manual lead creation, webhooks and Google Sheets imports already
  // behave) — checking this box explicitly opts into simple round-robin
  // distribution instead, which bypasses those rules.
  const [distributeEqually, setDistributeEqually] = useState(false);
  const [importProductIds, setImportProductIds] = useState([]);
  // Country used for phone numbers written without a country code
  const [importCountry, setImportCountry] = useState(getSavedPhoneCountry);
  const [importing, setImporting] = useState(false);
  // Export popup: null = closed, otherwise the scope it opens with
  const [exportScope, setExportScope] = useState(null);
  // Bumped on every file upload so the preview's tabs / edits start fresh
  const [importUploadId, setImportUploadId] = useState(0);
  const [validationResults, setValidationResults] = useState(null);
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState(""); // 'asc' | 'desc'
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // single lead id or 'bulk'
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);

  // Quick action states
  const [openMenuLeadId, setOpenMenuLeadId] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ bottom: 0, left: 0 });
  const [showQuickStageModal, setShowQuickStageModal] = useState(false);
  const [showQuickStatusModal, setShowQuickStatusModal] = useState(false);
  const [showQuickNoteModal, setShowQuickNoteModal] = useState(false);
  const [showQuickFollowUpModal, setShowQuickFollowUpModal] = useState(false);
  const [showQuickEmailModal, setShowQuickEmailModal] = useState(false);

  // Close dropdown on outside click or scroll
  useEffect(() => {
    if (!openMenuLeadId) return;
    const handleClickOutside = (e) => {
      // Don't close if clicking inside the dropdown menu
      if (e.target.closest('[data-quick-menu]')) return;
      setOpenMenuLeadId(null);
    };
    const handleScroll = () => setOpenMenuLeadId(null);
    document.addEventListener("click", handleClickOutside);
    document.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleScroll);
    return () => {
      document.removeEventListener("click", handleClickOutside);
      document.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleScroll);
    };
  }, [openMenuLeadId]);

  // Calculate and position the dropdown menu
  const openMenu = useCallback((leadId, lead, e) => {
    e.stopPropagation();
    if (openMenuLeadId === leadId) {
      setOpenMenuLeadId(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    // Position menu above the button, aligned to the right edge
    const menuWidth = 208; // w-52 = 13rem = 208px
    const menuHeight = 260; // approximate height of the dropdown
    const left = Math.max(8, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8));
    // Place the bottom of the menu just above the button top
    const bottom = window.innerHeight - rect.top + 4; // 4px gap above button
    setMenuPosition({ bottom, left });
    setSelectedLead(lead);
    setOpenMenuLeadId(leadId);
  }, [openMenuLeadId]);

  // Fetch pipeline stages for dynamic tabs and filters
  const { data: stagesData } = useQuery({
    queryKey: ["pipelineStages"],
    queryFn: async () => {
      const response = await pipelineAPI.getAll();
      return response.data?.data || [];
    },
  });

  // Create stage options for dropdown and mapping
  const stages = stagesData || [];

  // Fetch active products for optional product selection on import
  const { data: productsData } = useQuery({
    queryKey: ["products", "active"],
    queryFn: async () => {
      const response = await productsAPI.getAll({ active: "true" });
      return response.data?.data || [];
    },
  });
  const products = productsData || [];

  const stageOptions = stages.map((stage) => ({
    value: stage.key,
    label: stage.name,
    color: stage.color,
  }));

  // Create a map from stage key to stage info
  const stageMap = stages.reduce((acc, stage) => {
    acc[stage.key] = {
      name: stage.name,
      color: stage.color || "#6B7280",
    };
    return acc;
  }, {});

  // Fetch leads
  const { data: leadsData, isLoading } = useQuery({
    queryKey: ["leads", page, pageSize, activeTab, statusFilter, assignmentFilter, personFilter, search.trim(), sortBy, sortOrder],
    queryFn: async () => {
      const params = { page, limit: pageSize };
      // activeTab is the single stage filter, applied on the backend so it
      // spans all data — not just the currently loaded page.
      if (activeTab && activeTab !== "all") params.stage = activeTab;
      if (statusFilter) params.status = statusFilter;
      if (assignmentFilter) params.assignment = assignmentFilter;
      if (personFilter) params.assignedTo = personFilter;
      const trimmedSearch = search.trim();
      if (trimmedSearch) params.search = trimmedSearch;
      if (sortBy) params.sortBy = sortBy;
      if (sortOrder) params.sortOrder = sortOrder;

      const response = await leadsAPI.getAll(params);
      return response.data || {};
    },
  });

  // Fetch stage stats for counts (all leads, no date filter)
  const { data: stageStats } = useQuery({
    queryKey: ["stageStats"],
    queryFn: async () => {
      const response = await pipelineAPI.getStats({ range: 'all' });
      return response.data?.data || [];
    },
  });

  // Legacy: Fetch leads counts (for backward compatibility)
  const { data: countsData } = useQuery({
    queryKey: ["leadsCounts"],
    queryFn: async () => {
      const response = await leadsAPI.getCounts();
      return response.data?.data || {};
    },
  });

  // Fetch all leads for duplicate checking (cached)
  const { data: allLeadsData } = useQuery({
    queryKey: ["allLeadsForValidation"],
    queryFn: async () => {
      const response = await leadsAPI.getAll({ limit: 10000 });
      return response.data?.data || [];
    },
    staleTime: 30000, // Cache for 30 seconds
    enabled: !isTelecaller, // only used by the admin import flow
  });

  // Get existing phone numbers for duplicate check
  const existingPhones = useMemo(() => {
    if (!allLeadsData) return new Set();
    const phones = new Set();
    allLeadsData.forEach((lead) => {
      if (lead.phone) {
        phones.add(lead.phone.replace(/[\s\-]/g, "").replace(/^\+91/, ""));
      }
      if (lead.alternatePhone) {
        phones.add(lead.alternatePhone.replace(/[\s\-]/g, "").replace(/^\+91/, ""));
      }
    });
    return phones;
  }, [allLeadsData]);

  // Fetch total leads count with current filters
  const { data: filteredCountData } = useQuery({
    queryKey: [
      "filteredLeadsCount",
      activeTab,
      statusFilter,
      assignmentFilter,
      personFilter,
      search.trim(),
    ],
    queryFn: async () => {
      const params = { limit: 1 };
      if (activeTab && activeTab !== "all") params.stage = activeTab;
      if (statusFilter) params.status = statusFilter;
      if (assignmentFilter) params.assignment = assignmentFilter;
      if (personFilter) params.assignedTo = personFilter;
      const trimmedSearch = search.trim();
      if (trimmedSearch) params.search = trimmedSearch;

      const response = await leadsAPI.getAll(params);
      return response.data?.pagination?.total || 0;
    },
  });

  // Fetch users for assignment
  const { data: usersData } = useQuery({
    queryKey: ["users"],
    queryFn: async () => {
      const response = await usersAPI.getAll({ role: "telecaller" });
      return response.data?.data || [];
    },
    enabled: !isTelecaller,
  });

  // Delete lead mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => leadsAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["leads"]);
      queryClient.invalidateQueries(["leadsCounts"]);
      toast.success("Lead deleted successfully");
    },
    onError: () => toast.error("Failed to delete lead"),
  });

  // Bulk delete leads mutation
  const bulkDeleteMutation = useMutation({
    mutationFn: (ids) => leadsAPI.bulkDelete(ids),
    onSuccess: (response) => {
      const deletedCount =
        response?.data?.deletedCount ?? selectedLeads.length;
      queryClient.invalidateQueries(["leads"]);
      queryClient.invalidateQueries(["leadsCounts"]);
      queryClient.invalidateQueries(["stageStats"]);
      toast.success(`${deletedCount} leads deleted successfully`);
      setSelectedLeads([]);
      setSelectionMode("page");
    },
    onError: (error) =>
      toast.error(
        error?.response?.data?.message || "Failed to delete leads",
      ),
  });

  // Bulk update stage mutation
  const bulkUpdateStageMutation = useMutation({
    mutationFn: ({ ids, stage }) => leadsAPI.bulkUpdate({ ids, stage }),
    onSuccess: () => {
      queryClient.invalidateQueries(["leads"]);
      queryClient.invalidateQueries(["stageStats"]);
      toast.success("Stage updated successfully");
      setSelectedLeads([]);
      setSelectionMode("page");
      setShowBulkStageModal(false);
    },
    onError: (error) =>
      toast.error(error?.response?.data?.message || "Failed to update stage"),
  });

  // Bulk update status mutation
  const bulkUpdateStatusMutation = useMutation({
    mutationFn: ({ ids, status }) => leadsAPI.bulkUpdate({ ids, status }),
    onSuccess: () => {
      queryClient.invalidateQueries(["leads"]);
      toast.success("Status updated successfully");
      setSelectedLeads([]);
      setSelectionMode("page");
      setShowBulkStatusModal(false);
    },
    onError: (error) =>
      toast.error(error?.response?.data?.message || "Failed to update status"),
  });

  // Quick action mutations
  const quickUpdateStageMutation = useMutation({
    mutationFn: ({ id, stage }) => leadsAPI.update(id, { stage }),
    onSuccess: () => {
      queryClient.invalidateQueries(["leads"]);
      queryClient.invalidateQueries(["stageStats"]);
      toast.success("Stage updated successfully");
      setShowQuickStageModal(false);
      setSelectedLead(null);
    },
    onError: () => toast.error("Failed to update stage"),
  });

  const quickUpdateStatusMutation = useMutation({
    mutationFn: ({ id, status }) => leadsAPI.update(id, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries(["leads"]);
      toast.success("Status updated successfully");
      setShowQuickStatusModal(false);
      setSelectedLead(null);
    },
    onError: () => toast.error("Failed to update status"),
  });

  const quickAddNoteMutation = useMutation({
    mutationFn: ({ id, note, file }) => {
      if (file) {
        const formData = new FormData();
        formData.append("note", note);
        formData.append("attachments", file);
        return leadsAPI.addNote(id, formData);
      }
      return leadsAPI.addNote(id, { note });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["leads"]);
      toast.success("Note added successfully");
      setShowQuickNoteModal(false);
      setSelectedLead(null);
    },
    onError: () => toast.error("Failed to add note"),
  });

  const quickFollowUpMutation = useMutation({
    mutationFn: (data) => followUpsAPI.create(data),
    onSuccess: () => {
      toast.success("Follow-up scheduled successfully");
      setShowQuickFollowUpModal(false);
      setSelectedLead(null);
    },
    onError: () => toast.error("Failed to schedule follow-up"),
  });

  const quickSendEmailMutation = useMutation({
    mutationFn: ({ templateId, data }) => emailTemplatesAPI.send(templateId, data),
    onSuccess: (response) => {
      toast.success(response.data?.message || "Email sent successfully");
      setShowQuickEmailModal(false);
      setSelectedLead(null);
    },
    onError: (error) =>
      toast.error(
        error.response?.data?.message || error.message || "Failed to send email",
      ),
  });

  const quickSendCustomEmailMutation = useMutation({
    mutationFn: (data) => emailTemplatesAPI.sendCustom(data),
    onSuccess: (response) => {
      toast.success(response.data?.message || "Email sent successfully");
      setShowQuickEmailModal(false);
      setSelectedLead(null);
    },
    onError: (error) =>
      toast.error(
        error.response?.data?.message || error.message || "Failed to send email",
      ),
  });

  const leads = leadsData?.data || [];
  const pagination = leadsData?.pagination || {};

  // Calculate total leads from stage stats
  const totalLeadsFromStages =
    stageStats?.reduce((sum, stage) => sum + (stage.leadsCount || 0), 0) || 0;

  // Create tabs from stage stats — show ALL stages (horizontally scrollable)
  const tabs = [
    { value: "all", label: "All", count: totalLeadsFromStages },
    ...(stageStats || []).map((stage) => ({
      value: stage.key,
      label: stage.name,
      count: stage.leadsCount || 0,
      color: stage.color,
    })),
  ];

  // Stage filtering is now done on the backend (activeTab -> params.stage),
  // so the loaded page already contains only the matching leads. No
  // client-side re-filtering needed.
  const filteredLeads = leads;

  const handleSelectLead = (id) => {
    setSelectedLeads((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleSelectAll = () => {
    if (selectedLeads.length === leads.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(leads.map((l) => l._id));
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      // Cycle: asc → desc → clear
      if (sortOrder === "asc") {
        setSortOrder("desc");
      } else {
        // desc → clear (no sort)
        setSortBy("");
        setSortOrder("");
      }
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
    setPage(1); // Reset to first page on sort change
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) {
      return (
        <span className="inline-flex flex-col ml-0.5 opacity-30">
          <HiOutlineChevronUp className="w-3 h-3 -mb-1" />
          <HiOutlineChevronDown className="w-3 h-3" />
        </span>
      );
    }
    return sortOrder === "asc" ? (
      <HiOutlineChevronUp className="w-3.5 h-3.5 ml-0.5 text-primary-600" />
    ) : (
      <HiOutlineChevronDown className="w-3.5 h-3.5 ml-0.5 text-primary-600" />
    );
  };

  const handleDelete = (id) => {
    setDeleteTarget(id);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    if (deleteTarget) {
      deleteMutation.mutate(deleteTarget);
    }
    setShowDeleteConfirm(false);
    setDeleteTarget(null);
  };

  const handleBulkDelete = () => {
    if (selectedLeads.length === 0) {
      toast.error("Select leads to delete");
      return;
    }
    setShowBulkDeleteConfirm(true);
  };

  const confirmBulkDelete = () => {
    bulkDeleteMutation.mutate(selectedLeads);
    setShowBulkDeleteConfirm(false);
  };

  const handleSingleAssign = (lead) => {
    setSelectedLeadForAssign(lead);
    setShowSingleAssignModal(true);
  };

  // Parse CSV file
  const parseCSV = (text) => {
    const lines = text.trim().split("\n");
    if (lines.length < 2) {
      toast.error("CSV file must have headers and at least one data row");
      return [];
    }

    const headers = lines[0].split(",").map((h) => h.trim().replace(/"/g, ""));
    const data = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(",").map((v) => v.trim().replace(/"/g, ""));
      const row = {};
      headers.forEach((header, index) => {
        row[header] = values[index] || "";
      });
      if (row.name || row.Name || row.NAME) {
        data.push(row);
      }
    }

    return data;
  };

  // Download a ready-to-fill Excel template for the import modal — a real
  // .xlsx workbook (not CSV) so phone numbers stay text (no more Excel
  // scientific-notation like 9.877E+09) and there's a second sheet listing
  // every valid stage/status/priority/source value.
  // Sheet 1 columns: name, phone (required), alternatePhone/email/notes
  // (optional), stage, status, source and priority — the same headers
  // handleFileUpload's validators expect.
  const handleDownloadTemplate = () => {
    const headers = ["name", "phone", "alternatePhone", "email", "stage", "status", "source", "priority", "notes"];
    const exampleRows = [
      ["John Doe", "9876543210", "9876543211", "john@email.com", "new_lead", "new", "Website", "high", "Interested in premium plan"],
      ["Jane Smith", "9876543211", "", "jane@email.com", "connected", "warm", "Referral", "medium", ""],
      ["Amit Kumar", "9876543212", "", "amit@email.com", "qualified", "hot", "Cold Call", "urgent", "Ready to close this week"],
    ];

    const templateSheet = XLSX.utils.aoa_to_sheet([headers, ...exampleRows]);
    // Force the phone / alternatePhone columns (B, C) to text so Excel never
    // converts long numbers to scientific notation.
    exampleRows.forEach((_, rowIdx) => {
      ["B", "C"].forEach((col) => {
        const ref = `${col}${rowIdx + 2}`; // +2: row 1 is the header
        if (templateSheet[ref]) {
          templateSheet[ref].t = "s";
          templateSheet[ref].z = "@";
        }
      });
    });
    templateSheet["!cols"] = [
      { wch: 16 }, { wch: 14 }, { wch: 16 }, { wch: 22 },
      { wch: 20 }, { wch: 12 }, { wch: 14 }, { wch: 10 }, { wch: 30 },
    ];

    // Sheet 2 — every valid value for Stage / Status / Priority / Source, so
    // there's no guessing while filling in Sheet 1.
    const referenceRows = [
      ["Field", "Value", "Meaning"],
      ["Stage", "new_lead", "New Lead (default if left blank)"],
      ["Stage", "contact_attempted", "Contact Attempted"],
      ["Stage", "not_connected", "Not Connected"],
      ["Stage", "connected", "Connected"],
      ["Stage", "qualified", "Qualified"],
      ["Stage", "follow_up_required", "Follow-Up Required"],
      ["Stage", "meeting_scheduled", "Meeting Scheduled"],
      ["Stage", "proposal_sent", "Proposal Sent"],
      ["Stage", "negotiation", "Negotiation"],
      ["Stage", "converted", "Converted"],
      ["Stage", "lost", "Lost"],
      ["Stage", "not_interested", "Not Interested"],
      ["Status", "new", "New (default if left blank)"],
      ["Status", "open", "Open"],
      ["Status", "in_progress", "In Progress"],
      ["Status", "follow_up", "Follow Up"],
      ["Status", "cold", "Cold"],
      ["Status", "warm", "Warm"],
      ["Status", "hot", "Hot"],
      ["Status", "converted", "Converted"],
      ["Status", "closed", "Closed"],
      ["Status", "lost", "Lost"],
      ["Status", "not_connected", "Not Connected"],
      ["Priority", "urgent", "Urgent"],
      ["Priority", "high", "High"],
      ["Priority", "medium", "Medium (default if left blank)"],
      ["Priority", "low", "Low"],
      ["Source", "Website", "Matches Leads → Auto-Assign Rules by source"],
      ["Source", "Referral", "Matches Leads → Auto-Assign Rules by source"],
      ["Source", "Cold Call", "Matches Leads → Auto-Assign Rules by source"],
      ["Source", "LinkedIn", "Matches Leads → Auto-Assign Rules by source"],
      ["Source", "Email Campaign", "Matches Leads → Auto-Assign Rules by source"],
      ["Source", "Social Media", "Matches Leads → Auto-Assign Rules by source"],
      ["Source", "Advertisement", "Matches Leads → Auto-Assign Rules by source"],
      ["Source", "Walk-in", "Matches Leads → Auto-Assign Rules by source"],
      ["Source", "Phone Inquiry", "Matches Leads → Auto-Assign Rules by source"],
      ["Source", "Other", "Matches Leads → Auto-Assign Rules by source"],
      ["Source", "(any other text)", "Accepted, but won't match an Auto-Assign Rule by source"],
    ];
    const referenceSheet = XLSX.utils.aoa_to_sheet(referenceRows);
    referenceSheet["!cols"] = [{ wch: 12 }, { wch: 22 }, { wch: 48 }];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, templateSheet, "Leads Template");
    XLSX.utils.book_append_sheet(workbook, referenceSheet, "Valid Values");

    XLSX.writeFile(workbook, "leads-import-template.xlsx");
    toast.success("Template downloaded");
  };

  // Validates import rows (name, phone per country, email, stage, duplicates
  // in file / in system). Used on upload and again after edits in the preview.
  const validateImportRows = (rows, defaultCountry = importCountry) => {
    // Validate each lead
    const validatedLeads = rows.map((lead, index) => {
      const errors = [];
      const warnings = [];
      // rawPhone / rawAlternatePhone hold the original (or edited) values so
      // re-validation never runs on an already-normalized number
      const rawPhone = lead.rawPhone !== undefined ? lead.rawPhone : (
        lead.phone ||
        lead.Phone ||
        lead.PHONE ||
        lead.mobile ||
        lead.Mobile ||
        "");
      // An edited (even emptied) name/email wins over the file's other columns
      const rawName = lead.name !== undefined ? lead.name : (lead.Name || lead.NAME || "");
      const rawAlternatePhone = lead.rawAlternatePhone !== undefined ? lead.rawAlternatePhone : (
        lead.alternatePhone ||
        lead["Alternate Phone"] ||
        lead.alternate_phone ||
        lead.secondaryPhone ||
        lead["Secondary Phone"] ||
        lead.secondary_phone ||
        "");
      // Per-row country (bulk edit in the preview) overrides the modal's one
      const rowCountry = lead.phoneCountry || defaultCountry;
      const rawEmail = lead.email !== undefined ? lead.email : (lead.Email || lead.EMAIL || "");
      // Stage (pipeline stage) and status (call disposition) are separate
      // columns/concepts — see validateStage/validateStatus above.
      const rawStage = lead.stage || lead.Stage || lead.STAGE || "";
      const rawStatus = lead.status || lead.Status || lead.STATUS || "";
      const rawSource = lead.source || lead.Source || lead.SOURCE || "";
      const rawPriority = lead.priority || lead.Priority || lead.PRIORITY || "";

      // Validate name
      const nameValidation = validateName(rawName);
      if (!nameValidation.isValid) {
        errors.push(`Name: ${nameValidation.error}`);
      }

      // Validate phone
      const phoneValidation = validateInternationalPhone(rawPhone, rowCountry);
      if (!phoneValidation.isValid) {
        errors.push(`Phone: ${phoneValidation.error}`);
      }

      // Validate alternatePhone (optional)
      const alternatePhoneValidation = validateOptionalInternationalPhone(rawAlternatePhone, rowCountry);
      if (!alternatePhoneValidation.isValid) {
        errors.push(`Alternate Phone: ${alternatePhoneValidation.error}`);
      }

      // Validate email if provided
      const emailValidation = validateEmail(rawEmail);
      if (!emailValidation.isValid) {
        errors.push(`Email: ${emailValidation.error}`);
      }

      // Validate stage (optional — defaults to the org's "New" stage)
      const stageValidation = validateStage(rawStage);
      if (!stageValidation.isValid) {
        errors.push(`Stage: ${stageValidation.error}`);
      }

      // Status/priority never hard-fail the row — unrecognized values just
      // fall back to a sensible default, surfaced as a warning.
      const statusValidation = validateStatus(rawStatus);
      if (statusValidation.warning) warnings.push(statusValidation.warning);
      const priorityValidation = validatePriority(rawPriority);
      if (priorityValidation.warning) warnings.push(priorityValidation.warning);

      return {
        ...lead,
        rowNum: lead.rowNum || index + 2,
        name: nameValidation.isValid ? nameValidation.cleanName : rawName,
        phone: phoneValidation.isValid
          ? phoneValidation.cleanPhone
          : rawPhone,
        alternatePhone: alternatePhoneValidation.isValid
          ? alternatePhoneValidation.cleanPhone
          : rawAlternatePhone,
        rawPhone: rawPhone,
        rawAlternatePhone: rawAlternatePhone,
        email: emailValidation.isValid
          ? emailValidation.cleanEmail
          : rawEmail,
        stage: stageValidation.isValid
          ? stageValidation.cleanStage
          : rawStage,
        status: statusValidation.cleanStatus,
        source: rawSource || undefined,
        priority: priorityValidation.cleanPriority,
        rawStatus: rawStatus,
        isValid: errors.length === 0,
        errors,
        warnings,
        isDuplicateInFile: false,
        isDuplicateInSystem: false,
      };
    });

    // Check for duplicates within file
    const phoneCounts = {};
    validatedLeads.forEach((lead) => {
      if (lead.phone && lead.isValid) {
        phoneCounts[lead.phone] = (phoneCounts[lead.phone] || 0) + 1;
      }
      if (lead.alternatePhone && lead.isValid) {
        phoneCounts[lead.alternatePhone] = (phoneCounts[lead.alternatePhone] || 0) + 1;
      }
    });

    // Mark duplicates within file
    validatedLeads.forEach((lead) => {
      if (lead.phone && phoneCounts[lead.phone] > 1) {
        lead.isDuplicateInFile = true;
        lead.isValid = false;
        if (!lead.errors.includes("Duplicate phone in file")) {
          lead.errors.push("Duplicate phone in file");
        }
      }
      if (lead.alternatePhone && phoneCounts[lead.alternatePhone] > 1) {
        lead.isDuplicateInFile = true;
        lead.isValid = false;
        if (!lead.errors.includes("Duplicate alternate phone in file")) {
          lead.errors.push("Duplicate alternate phone in file");
        }
      }
    });

    // Check for duplicates in system
    validatedLeads.forEach((lead) => {
      if (lead.phone && lead.isValid && existingPhones.has(lead.phone)) {
        lead.isDuplicateInSystem = true;
        lead.isValid = false;
        if (!lead.errors.includes("Phone already exists in system")) {
          lead.errors.push("Phone already exists in system");
        }
      }
      if (lead.alternatePhone && lead.isValid && existingPhones.has(lead.alternatePhone)) {
        lead.isDuplicateInSystem = true;
        lead.isValid = false;
        if (!lead.errors.includes("Alternate phone already exists in system")) {
          lead.errors.push("Alternate phone already exists in system");
        }
      }
    });

    return validatedLeads;
  };

  // Validates rows and stores them + the summary; returns the validated rows
  const applyImportValidation = (rows, defaultCountry = importCountry) => {
    const validatedLeads = validateImportRows(rows, defaultCountry);
    setImportData(validatedLeads);
    const validCount = validatedLeads.filter((l) => l.isValid).length;
    const invalidCount = validatedLeads.length - validCount;
    setValidationResults(
      invalidCount > 0
        ? {
            total: validatedLeads.length,
            valid: validCount,
            invalid: invalidCount,
            duplicates: validatedLeads.filter((l) => l.isDuplicateInSystem).length,
            duplicateInFile: validatedLeads.filter((l) => l.isDuplicateInFile).length,
          }
        : null,
    );
    return validatedLeads;
  };

  // Handle file upload — accepts both .csv and .xlsx/.xls (the downloadable
  // template is now a real Excel workbook; see handleDownloadTemplate).
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const isExcel = /\.(xlsx|xls)$/i.test(file.name);
    const isCsv = /\.csv$/i.test(file.name);
    if (!isExcel && !isCsv) {
      toast.error("Please upload a CSV or Excel (.xlsx) file");
      return;
    }

    const processRows = (parsed) => {
      if (parsed.length === 0) {
        toast.error("No valid leads found in the file");
        return;
      }

      const validatedLeads = applyImportValidation(parsed);
      setImportUploadId((n) => n + 1);
      const invalidCount = validatedLeads.filter((l) => !l.isValid).length;
      if (invalidCount > 0) {
        toast.error(
          `Found ${invalidCount} invalid/duplicate leads. Fix them in the "Invalid" tab or import only the valid ones.`,
        );
      } else {
        toast.success(`Found ${validatedLeads.length} valid leads ready to import`);
      }
    };

    const reader = new FileReader();
    if (isExcel) {
      reader.onload = (event) => {
        const workbook = XLSX.read(event.target.result, { type: "array" });
        // Prefer the "Leads Template" sheet; skip the "Valid Values" reference
        // sheet if this is our own downloaded template re-uploaded as-is.
        const sheetName =
          workbook.SheetNames.find((n) => n.toLowerCase() === "leads template") ||
          workbook.SheetNames.find((n) => n.toLowerCase() !== "valid values") ||
          workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        // raw: false keeps phone numbers as their displayed text, never as JS numbers
        const parsed = XLSX.utils.sheet_to_json(sheet, { defval: "", raw: false });
        processRows(parsed);
      };
      reader.readAsArrayBuffer(file);
    } else {
      reader.onload = (event) => {
        processRows(parseCSV(event.target.result));
      };
      reader.readAsText(file);
    }
  };

  // Handle import
  const handleImport = async () => {
    if (importData.length === 0) {
      toast.error("No data to import");
      return;
    }

    // Only import valid leads
    const validLeads = importData.filter((lead) => lead.isValid);

    if (validLeads.length === 0) {
      toast.error("No valid leads to import. Please fix errors and try again.");
      return;
    }

    setImporting(true);
    try {
      const response = await leadsAPI.importLeads({
        leads: validLeads.map((lead) => ({
          name: lead.name,
          phone: lead.phone,
          alternatePhone: lead.alternatePhone || undefined,
          phoneCountry: lead.phoneCountry || undefined,
          email: lead.email,
          // No stage in the file → backend resolves the org's own "New" stage
          // key ("new" / "new_lead" / custom). Hard-coding "new_lead" broke
          // assignment rules that filter on the org's "new" stage.
          stage: lead.stage || undefined,
          status: lead.status || "new",
          source: lead.source || undefined,
          priority: lead.priority || "medium",
          notes: lead.notes || lead.Notes,
          // Optional assignee column (email / full name / phone). The backend
          // resolves it only within this organization's active users.
          assignedTo:
            lead.assignedTo || lead["Assigned To"] || lead.assignedToEmail || lead.assigned_to || undefined,
        })),
        distributeEqually: distributeEqually,
        productIds: importProductIds.length > 0 ? importProductIds : undefined,
        defaultCountry: importCountry,
      });

      if (response.data?.success) {
        const { totalImported, telecallerCount, totalSkipped } =
          response.data.data;
        let message = `${totalImported} leads imported`;
        if (distributeEqually && telecallerCount) {
          message += ` and distributed to ${telecallerCount} telecallers`;
        }
        if (totalSkipped > 0) {
          message += `. ${totalSkipped} leads were skipped due to validation errors.`;
        }
        toast.success(message);
        const { assignedCount } = response.data.data;
        if (typeof assignedCount === "number" && assignedCount < totalImported) {
          toast(
            `${totalImported - assignedCount} lead(s) were not assigned — no assignment rule matched and Auto-Assign is off. Check Leads → Assignment Rules.`,
            { icon: "⚠️", duration: 8000 },
          );
        }
        const assignWarnings = response.data.data?.assignmentWarnings || [];
        if (assignWarnings.length > 0) {
          const lines = assignWarnings
            .slice(0, 5)
            .map((w) => `${w.name || w.phone}: ${w.reason}`)
            .join("\n");
          toast.error(
            `${assignWarnings.length} lead(s) had an assignee that is not in your organization's user list:\n${lines}${assignWarnings.length > 5 ? "\n…" : ""}`,
            { duration: 10000 },
          );
        }
        setShowImportModal(false);
        setImportData([]);
        setImportProductIds([]);
        setValidationResults(null);
        queryClient.invalidateQueries(["leads"]);
        queryClient.invalidateQueries(["leadsCounts"]);
        queryClient.invalidateQueries(["allLeadsForValidation"]);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    } catch (error) {
      const errorData = error.response?.data;
      if (errorData?.code === 'LIMIT_EXCEEDED') {
        toast.error(errorData.message || 'Contact limit reached. Please upgrade your plan.');
      } else if (errorData?.data?.errors) {
        // Display validation errors from backend
        const errorMessages = errorData.data.errors
          .slice(0, 5)
          .map((e) => `Row ${e.row}: ${e.errors.join(", ")}`)
          .join("\n");
        toast.error(`${errorData.message}\n${errorMessages}`);
      } else {
        toast.error(errorData?.message || "Failed to import leads");
      }
    } finally {
      setImporting(false);
    }
  };

  // Kanban view groups leads by pipeline stage with drag & drop stage updates
  const [viewMode, setViewMode] = useState("list");

  return (
    <PageLayout>
      <div className="p-4 lg:p-6 space-y-4 sm:space-y-6">
        {/* Leads section tabs (Leads / Smart Lists / Re-engagement / Territories / Auto-Assign Rules) */}
        {!isTelecaller && <LeadsTabs />}
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
          <div className="min-w-0">
            <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">
              {isTelecaller ? "My Leads" : "Leads"}
            </h1>
            <p className="text-gray-500 text-xs sm:text-sm mt-0.5 sm:mt-1">
              {isTelecaller
                ? `${totalLeadsFromStages} lead${totalLeadsFromStages === 1 ? "" : "s"} assigned to you`
                : "Manage and assign leads to telecallers"}
            </p>
          </div>
          <div className="flex gap-1.5 sm:gap-2 flex-wrap">
            {/* View toggle: List | Kanban */}
            <div className="flex rounded-lg border border-gray-200 overflow-hidden self-start">
              <button
                type="button"
                onClick={() => setViewMode("list")}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs sm:text-sm font-medium transition-colors ${
                  viewMode === "list"
                    ? "bg-primary-600 text-white"
                    : "bg-white text-gray-600 hover:bg-gray-50"
                }`}
                title="List view"
              >
                <HiOutlineViewList className="w-4 h-4" />
                <span className="hidden md:inline">List</span>
              </button>
              <button
                type="button"
                onClick={() => setViewMode("kanban")}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs sm:text-sm font-medium transition-colors border-l border-gray-200 ${
                  viewMode === "kanban"
                    ? "bg-primary-600 text-white"
                    : "bg-white text-gray-600 hover:bg-gray-50"
                }`}
                title="Kanban view by stage — drag cards to change stage"
              >
                <HiOutlineViewBoards className="w-4 h-4" />
                <span className="hidden md:inline">Kanban</span>
              </button>
            </div>
            {!isTelecaller && (
            <>
            <Tooltip text="Bulk select leads for batch actions">
              <Button
                variant="outline"
                size="sm"
                icon={<HiOutlineClipboardCheck className="w-4 h-4" />}
                onClick={() => setShowBulkSelectModal(true)}
              >
                <span className="hidden sm:inline">Bulk Select</span>
                <span className="sm:hidden">Bulk</span>
              </Button>
            </Tooltip>
            <Tooltip text="Import leads from a CSV file">
              <Button
                variant="outline"
                size="sm"
                icon={<HiOutlineUpload className="w-4 h-4" />}
                onClick={() => setShowImportModal(true)}
              >
                <span className="hidden sm:inline">Import</span>
              </Button>
            </Tooltip>
            <Tooltip text="Export leads by stage, status, date, assignee and more">
              <Button
                variant="outline"
                size="sm"
                icon={<HiOutlineDownload className="w-4 h-4" />}
                aria-label="Export leads"
                onClick={() =>
                  setExportScope(
                    (activeTab && activeTab !== "all") || statusFilter || assignmentFilter || personFilter || search.trim()
                      ? "current"
                      : "all",
                  )
                }
              >
                <span className="hidden sm:inline">Export</span>
              </Button>
            </Tooltip>
            <Tooltip text="Add a new lead">
              <Button
                variant="primary"
                size="sm"
                icon={<HiOutlinePlus className="w-4 h-4" />}
                onClick={() => navigate("/app/leads/assign")}
              >
                <span className="hidden sm:inline">Add Lead</span>
                <span className="sm:hidden">Add</span>
              </Button>
            </Tooltip>
            </>
            )}
          </div>
        </div>

        {/* List view (stage tabs + filters + table) */}
        {viewMode === "list" && (
          <>
        {/* Tabs */}
        <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

        {/* Filters */}
        <Card padding="p-3 sm:p-4">
          <div className="flex flex-col justify-between sm:flex-row gap-2 sm:gap-3">
            <div className="flex-1 max-w-[700px]">
              <SearchInput
                value={search}
                onChange={setSearch}
                placeholder="Search leads..."
              />
            </div>
            <div className="flex gap-2">
              <Select
                options={[{ value: "all", label: "All Stages" }, ...stageOptions]}
                value={activeTab}
                onChange={(e) => setActiveTab(e.target.value)}
                className="flex-1 min-w-[150px]"
              />
              <Select
                options={[
                  { value: "", label: "All Status" },
                  ...statusOptions,
                ]}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="flex-1 min-w-[120px]"
              />
              {!isTelecaller && (
              <>
              <Select
                options={[
                  { value: "", label: "All Assignments" },
                  { value: "assigned", label: "Assigned" },
                  { value: "unassigned", label: "Unassigned" },
                ]}
                value={assignmentFilter}
                onChange={(e) => setAssignmentFilter(e.target.value)}
                className="flex-1 min-w-[150px]"
              />
              <Select
                options={[
                  { value: "", label: "All People" },
                  ...(usersData || []).map((u) => ({
                    value: u._id,
                    label: `${u.firstName} ${u.lastName}`,
                  })),
                ]}
                value={personFilter}
                onChange={(e) => setPersonFilter(e.target.value)}
                className="flex-1 min-w-[130px]"
              />
              </>
              )}
              <div className="flex items-center gap-1.5">
                <span className="text-xs sm:text-sm text-gray-500 whitespace-nowrap">Rows per page</span>
                <Select
                  options={[
                    { value: 25, label: "25" },
                    { value: 50, label: "50" },
                    { value: 100, label: "100" },
                    { value: 200, label: "200" },
                  ]}
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                  className="flex-1 min-w-[70px]"
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Bulk Actions */}
        {!isTelecaller && (selectedLeads.length > 0 || selectionMode === "count") && (
          <div className="flex items-center gap-2 sm:gap-3 p-2.5 sm:p-3 bg-primary-50 rounded-xl animate-fadeIn">
            <span className="text-xs sm:text-sm font-medium text-primary-700">
              {selectionMode === "count"
                ? `${bulkSelectCount} leads (by count)`
                : `${selectedLeads.length} selected`}
            </span>
            <Tooltip text="Assign selected leads to a telecaller">
              <Button
                variant="primary"
                size="sm"
                icon={<HiOutlineUserAdd className="w-4 h-4" />}
                onClick={() => setShowAssignModal(true)}
              >
                <span className="hidden sm:inline">Assign</span>
              </Button>
            </Tooltip>
            <Tooltip text="Change stage for selected leads">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowBulkStageModal(true)}
                disabled={selectedLeads.length === 0}
              >
                <span className="hidden sm:inline">Stage</span>
              </Button>
            </Tooltip>
            <Tooltip text="Change status for selected leads">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowBulkStatusModal(true)}
                disabled={selectedLeads.length === 0}
              >
                <span className="hidden sm:inline">Status</span>
              </Button>
            </Tooltip>
            <Tooltip text="Export selected leads to a CSV file">
              <Button
                variant="outline"
                size="sm"
                icon={<HiOutlineDownload className="w-4 h-4" />}
                onClick={() => setExportScope("selected")}
              >
                <span className="hidden sm:inline">Export</span>
              </Button>
            </Tooltip>
            <Tooltip text="Delete selected leads permanently">
              <Button
                variant="danger"
                size="sm"
                icon={<HiOutlineTrash className="w-4 h-4" />}
                onClick={handleBulkDelete}
                disabled={bulkDeleteMutation.isPending || selectedLeads.length === 0}
              >
                <span className="hidden sm:inline">
                  {bulkDeleteMutation.isPending ? "Deleting..." : "Delete"}
                </span>
              </Button>
            </Tooltip>
            <Tooltip text="Clear selection">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSelectedLeads([]);
                  setSelectionMode("page");
                }}
              >
                Clear
              </Button>
            </Tooltip>
          </div>
        )}

        {/* Leads List */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : filteredLeads.length === 0 ? (
          <Card>
            <EmptyState
              icon={<HiOutlineUserAdd className="w-8 h-8 text-gray-400" />}
              title="No leads found"
              description={isTelecaller ? "No leads match your filters" : "Add leads or adjust your filters"}
              action={isTelecaller ? undefined : (
                <Tooltip text="Add a new lead manually">
                  <Button
                    variant="primary"
                    onClick={() => navigate("/app/leads/assign")}
                  >
                    <div className="flex items-center">
                      <div>
                        <HiOutlinePlus className="w-4 h-4 mr-1" />
                      </div>
                      <div>Add Lead</div>
                    </div>
                  </Button>
                </Tooltip>
              )}
            />
          </Card>
        ) : (
          <Card padding="p-0">
            {/* Desktop Table View */}
            <div className="hidden md:block overflow-auto max-h-[calc(100vh-220px)]">
              <table className="w-full">
                <thead className="sticky top-0 z-10 bg-secondary-50 bg-gradient-to-r from-secondary-50 to-secondary-100 border-b-2 border-secondary-200/50">
                  <tr>
                    {!isTelecaller && (
                    <th className="px-4 py-3 lg:px-5 lg:py-3.5 text-left text-xs font-bold text-secondary-600 uppercase tracking-wider w-10">
                      <input
                        type="checkbox"
                        checked={selectedLeads.length === leads.length && leads.length > 0}
                        onChange={handleSelectAll}
                        className="w-4 h-4 rounded border-gray-300 text-primary-600"
                      />
                    </th>
                    )}
                    <th className="px-4 py-3 lg:px-5 lg:py-3.5 text-left text-xs font-bold text-secondary-600 uppercase tracking-wider whitespace-nowrap">Sr No.</th>
                    <th
                      className="px-4 py-3 lg:px-5 lg:py-3.5 text-left text-xs font-bold text-secondary-600 uppercase tracking-wider cursor-pointer select-none hover:text-primary-700 transition-colors whitespace-nowrap"
                      onClick={() => handleSort("name")}
                    >
                      <span className="inline-flex items-center">Name <SortIcon field="name" /></span>
                    </th>
                    <th className="px-4 py-3 lg:px-5 lg:py-3.5 text-left text-xs font-bold text-secondary-600 uppercase tracking-wider whitespace-nowrap">Phone</th>
                    <th className="px-4 py-3 lg:px-5 lg:py-3.5 text-left text-xs font-bold text-secondary-600 uppercase tracking-wider whitespace-nowrap">Alternate Phone</th>
                    {!isTelecaller && (
                    <th
                      className="px-4 py-3 lg:px-5 lg:py-3.5 text-left text-xs font-bold text-secondary-600 uppercase tracking-wider cursor-pointer select-none hover:text-primary-700 transition-colors whitespace-nowrap"
                      onClick={() => handleSort("assignedTo")}
                    >
                      <span className="inline-flex items-center">Assigned To <SortIcon field="assignedTo" /></span>
                    </th>
                    )}
                    <th
                      className="px-4 py-3 lg:px-5 lg:py-3.5 text-left text-xs font-bold text-secondary-600 uppercase tracking-wider cursor-pointer select-none hover:text-primary-700 transition-colors whitespace-nowrap"
                      onClick={() => handleSort("stage")}
                    >
                      <span className="inline-flex items-center">Stage <SortIcon field="stage" /></span>
                    </th>
                    <th
                      className="px-4 py-3 lg:px-5 lg:py-3.5 text-left text-xs font-bold text-secondary-600 uppercase tracking-wider cursor-pointer select-none hover:text-primary-700 transition-colors whitespace-nowrap"
                      onClick={() => handleSort("status")}
                    >
                      <span className="inline-flex items-center">Status <SortIcon field="status" /></span>
                    </th>
                    <th
                      className="px-4 py-3 lg:px-5 lg:py-3.5 text-left text-xs font-bold text-secondary-600 uppercase tracking-wider cursor-pointer select-none hover:text-primary-700 transition-colors whitespace-nowrap"
                      onClick={() => handleSort("createdAt")}
                    >
                      <span className="inline-flex items-center">Created <SortIcon field="createdAt" /></span>
                    </th>
                    <th className={`px-3 py-3 lg:px-4 lg:py-3.5 w-[168px] min-w-[168px] text-center text-xs font-bold text-secondary-600 uppercase tracking-wider whitespace-nowrap ${stickyActionHeaderClass}`}>Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-secondary-100">
                  {filteredLeads.map((lead, index) => (
                    <tr
                      key={lead._id}
                      className="transition-all duration-150 hover:bg-gradient-to-r hover:from-primary-50/30 hover:to-transparent cursor-pointer"
                      onClick={() => navigate(`/app/leads/${lead._id}`)}
                    >
                      {!isTelecaller && (
                      <td className="px-4 py-3 lg:px-5 lg:py-3.5" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedLeads.includes(lead._id)}
                          onChange={() => handleSelectLead(lead._id)}
                          onClick={(e) => e.stopPropagation()}
                          className="w-4 h-4 rounded border-gray-300 text-primary-600 cursor-pointer"
                        />
                      </td>
                      )}
                      <td className="px-4 py-3 lg:px-5 lg:py-3.5">
                        <span className="text-gray-500 text-sm">
                          {(pagination.page - 1) * pagination.limit + index + 1}
                        </span>
                      </td>
                      <td className="px-4 py-3 lg:px-5 lg:py-3.5">
                        <div className="font-medium text-gray-900 truncate" title={lead.name}>
                          {lead.name}
                        </div>
                        {lead.email && (
                          <div className="text-xs text-gray-500 truncate" title={lead.email}>
                            {lead.email}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 lg:px-5 lg:py-3.5">
                        <span className="text-gray-600 truncate block" title={lead.phone}>{lead.phone}</span>
                      </td>
                      <td className="px-4 py-3 lg:px-5 lg:py-3.5">
                        <span className={`truncate block ${lead.alternatePhone ? 'text-gray-500' : 'text-gray-300'}`} title={lead.alternatePhone || ''}>{lead.alternatePhone || '-'}</span>
                      </td>
                      {!isTelecaller && (
                      <td className="px-4 py-3 lg:px-5 lg:py-3.5">
                        {lead.assignedTo && typeof lead.assignedTo === "object" && (lead.assignedTo.firstName || lead.assignedTo.lastName) ? (
                          <span className="text-sm text-gray-700 truncate block" title={`${lead.assignedTo.firstName || ""} ${lead.assignedTo.lastName || ""}`.trim()}>
                            {lead.assignedTo.firstName}{" "}
                            {lead.assignedTo.lastName}
                          </span>
                        ) : lead.assignedTo ? (
                          <span className="text-sm text-gray-400 italic" title="The assigned user no longer exists">
                            Deleted user
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400 italic">
                            Unassigned
                          </span>
                        )}
                      </td>
                      )}
                      <td className="px-4 py-3 lg:px-5 lg:py-3.5">
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium capitalize truncate max-w-full"
                          title={stageMap[lead.stage]?.name || lead.stage?.replace(/_/g, " ") || "Unknown"}
                          style={{
                            backgroundColor: stageMap[lead.stage]?.color
                              ? `${stageMap[lead.stage].color}20`
                              : "#6B728020",
                            color: stageMap[lead.stage]?.color || "#6B7280",
                          }}
                        >
                          {stageMap[lead.stage]?.name ||
                            lead.stage?.replace(/_/g, " ") ||
                            "Unknown"}
                        </span>
                      </td>
                      <td className="px-4 py-3 lg:px-5 lg:py-3.5">
                        <span
                          className={`text-sm font-medium text-gray-900 status-${lead.status} truncate block`}
                          title={formatLabel(lead.status)}
                        >
                          {formatLabel(lead.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3 lg:px-5 lg:py-3.5">
                        <span className="text-gray-500 text-sm truncate block" title={lead.createdAt ? format(new Date(lead.createdAt), "MMM d, yyyy") : "-"}>
                          {lead.createdAt
                            ? format(new Date(lead.createdAt), "MMM d, yyyy")
                            : "-"}
                        </span>
                      </td>
                      <td className={`px-3 py-3 lg:px-4 lg:py-3.5 w-[168px] min-w-[168px] ${stickyActionCellClass}`}>
                        {/* Fixed-size, centred icon buttons so every row lines up
                            identically (the shared Button rendered an empty label
                            span + gap, which pushed icons off-centre). */}
                        <div
                          className="flex flex-nowrap items-center justify-center gap-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {!isTelecaller && (
                          <Tooltip position={index === 0 ? 'bottom' : 'top'} text={lead.assignedTo ? "Reassign Lead" : "Assign Lead"}>
                            <button
                              type="button"
                              aria-label={lead.assignedTo ? "Reassign lead" : "Assign lead"}
                              className={leadActionBtnClass}
                              onClick={() => handleSingleAssign(lead)}
                            >
                              <HiOutlineUserAdd className="w-4 h-4" />
                            </button>
                          </Tooltip>
                          )}
                          <Tooltip position={index === 0 ? 'bottom' : 'top'} text="Edit Lead">
                            <button
                              type="button"
                              aria-label="Edit lead"
                              className={leadActionBtnClass}
                              onClick={() => navigate(`/app/leads/${lead._id}`)}
                            >
                              <HiOutlinePencil className="w-4 h-4" />
                            </button>
                          </Tooltip>
                          <Tooltip position={index === 0 ? 'bottom' : 'top'} text="Delete Lead">
                            <button
                              type="button"
                              aria-label="Delete lead"
                              className={`${leadActionBtnClass} text-red-500 hover:text-red-600 hover:bg-red-50`}
                              onClick={() => handleDelete(lead._id)}
                            >
                              <HiOutlineTrash className="w-4 h-4" />
                            </button>
                          </Tooltip>
                          {/* Quick Actions Dropdown Trigger */}
                          <Tooltip position={index === 0 ? 'bottom' : 'top'} text="Quick Actions">
                            <button
                              type="button"
                              aria-label="Quick actions"
                              className={leadActionBtnClass}
                              onClick={(e) => openMenu(lead._id, lead, e)}
                            >
                              <HiOutlineDotsVertical className="w-4 h-4" />
                            </button>
                          </Tooltip>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden divide-y divide-gray-100">
              {filteredLeads.map((lead, index) => (
                <div
                  key={lead._id}
                  className="p-3 sm:p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/app/leads/${lead._id}`)}
                >
                  {/* Header Row */}
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div
                      className="flex items-center gap-2.5 sm:gap-3 min-w-0 flex-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <span className="text-xs text-gray-400 font-medium w-5 text-center flex-shrink-0">
                        {(pagination.page - 1) * pagination.limit + index + 1}
                      </span>
                      {!isTelecaller && (
                      <input
                        type="checkbox"
                        checked={selectedLeads.includes(lead._id)}
                        onChange={() => handleSelectLead(lead._id)}
                        className="w-4 h-4 rounded border-gray-300 text-primary-600 mt-0.5 cursor-pointer flex-shrink-0"
                      />
                      )}
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 truncate">
                          {lead.name}
                        </p>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <HiOutlinePhone className="w-3 h-3 text-gray-400 flex-shrink-0" />
                          <span className="text-xs sm:text-sm text-gray-500 truncate">
                            {lead.phone}
                          </span>
                        </div>
                        {lead.alternatePhone && (
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <HiOutlinePhone className="w-3 h-3 text-gray-300 flex-shrink-0" />
                            <span className="text-xs text-gray-400 truncate">
                              {lead.alternatePhone}
                            </span>
                          </div>
                        )}
                        {lead.email && (
                          <p className="text-xs text-gray-400 truncate">
                            {lead.email}
                          </p>
                        )}
                      </div>
                    </div>
                    <span
                      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium capitalize"
                      style={{
                        backgroundColor: stageMap[lead.stage]?.color
                          ? `${stageMap[lead.stage].color}20`
                          : "#6B728020",
                        color: stageMap[lead.stage]?.color || "#6B7280",
                      }}
                    >
                      {stageMap[lead.stage]?.name ||
                        lead.stage?.replace(/_/g, " ") ||
                        "Unknown"}
                    </span>
                  </div>

                  {/* Info Row */}
                  <div className="pl-6 sm:pl-7 flex items-center justify-between text-xs sm:text-sm">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <span className={`font-medium text-gray-900 status-${lead.status}`}>
                        {formatLabel(lead.status)}
                      </span>
                      {!isTelecaller && lead.assignedTo && (
                        <span className="text-gray-500 truncate">
                          • {lead.assignedTo.firstName}{" "}
                          {lead.assignedTo.lastName}
                        </span>
                      )}
                    </div>
                    <span className="text-gray-400 flex-shrink-0">
                      {lead.createdAt
                        ? format(new Date(lead.createdAt), "MMM d")
                        : "-"}
                    </span>
                  </div>

                  {/* Actions Row */}
                  <div
                    className="flex items-center justify-end gap-1 pt-2 mt-2 border-t border-gray-100"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {!isTelecaller && (
                    <Tooltip position={index === 0 ? 'bottom' : 'top'} text={lead.assignedTo ? "Reassign Lead" : "Assign Lead"}>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="p-2"
                        icon={
                          <HiOutlineUserAdd className="w-4 h-4 text-gray-500" />
                        }
                        onClick={() => handleSingleAssign(lead)}
                      />
                    </Tooltip>
                    )}
                    <Tooltip position={index === 0 ? 'bottom' : 'top'} text="Edit Lead">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="p-2"
                        icon={
                          <HiOutlinePencil className="w-4 h-4 text-gray-500" />
                        }
                        onClick={() => navigate(`/app/leads/${lead._id}`)}
                      />
                    </Tooltip>
                    <Tooltip position={index === 0 ? 'bottom' : 'top'} text="Delete Lead">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="p-2"
                        icon={<HiOutlineTrash className="w-4 h-4 text-red-500" />}
                        onClick={() => handleDelete(lead._id)}
                      />
                    </Tooltip>
                    {/* Quick Actions Dropdown Trigger (mobile) */}
                    <Tooltip position={index === 0 ? 'bottom' : 'top'} text="Quick Actions">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="p-2"
                        icon={<HiOutlineDotsVertical className="w-4 h-4 text-gray-500" />}
                        onClick={(e) => openMenu(`mobile-${lead._id}`, lead, e)}
                      />
                    </Tooltip>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 p-3 sm:p-4 border-t border-gray-100">
              <span className="text-xs sm:text-sm text-gray-500">
                {pagination.total > 0 ? (
                  <>Showing {(pagination.page - 1) * pagination.limit + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} leads</>
                ) : (
                  'No leads'
                )}
              </span>
              {pagination.totalPages > 1 && (
                <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap justify-center">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    <span className="hidden sm:inline">Previous</span>
                    <span className="sm:hidden">Prev</span>
                  </Button>

                  {buildPageList(pagination.page, pagination.totalPages).map(
                    (item, idx) =>
                      item === "..." ? (
                        <span
                          key={`ellipsis-${idx}`}
                          className="px-2 text-sm text-gray-400 select-none"
                        >
                          …
                        </span>
                      ) : (
                        <button
                          key={item}
                          type="button"
                          onClick={() => setPage(item)}
                          className={
                            page === item
                              ? "min-w-[34px] px-2.5 py-1.5 rounded-lg text-sm font-semibold bg-primary-600 text-white transition-colors"
                              : "min-w-[34px] px-2.5 py-1.5 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                          }
                        >
                          {item}
                        </button>
                      )
                  )}

                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === pagination.totalPages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              )}
            </div>
          </Card>
        )}
          </>
        )}

        {/* Kanban view — drag a lead card onto another stage column to update its stage */}
        {viewMode === "kanban" && (
          <LeadsKanban onLeadClick={(lead) => navigate(`/app/leads/${lead._id}`)} />
        )}

        {/* Quick Action Modals */}
        <QuickStageModal
          isOpen={showQuickStageModal}
          onClose={() => { setShowQuickStageModal(false); setSelectedLead(null); }}
          lead={selectedLead}
          stages={stages}
          onConfirm={(data) => quickUpdateStageMutation.mutate(data)}
          isLoading={quickUpdateStageMutation.isPending}
        />

        <QuickStatusModal
          isOpen={showQuickStatusModal}
          onClose={() => { setShowQuickStatusModal(false); setSelectedLead(null); }}
          lead={selectedLead}
          onConfirm={(data) => quickUpdateStatusMutation.mutate(data)}
          isLoading={quickUpdateStatusMutation.isPending}
        />

        <QuickNoteModal
          isOpen={showQuickNoteModal}
          onClose={() => { setShowQuickNoteModal(false); setSelectedLead(null); }}
          lead={selectedLead}
          onConfirm={(data) => quickAddNoteMutation.mutate(data)}
          isLoading={quickAddNoteMutation.isPending}
        />

        <QuickFollowUpModal
          isOpen={showQuickFollowUpModal}
          onClose={() => { setShowQuickFollowUpModal(false); setSelectedLead(null); }}
          lead={selectedLead}
          onConfirm={(data) => quickFollowUpMutation.mutate(data)}
          isLoading={quickFollowUpMutation.isPending}
        />
        <QuickSendEmailModal
          isOpen={showQuickEmailModal}
          onClose={() => { setShowQuickEmailModal(false); setSelectedLead(null); }}
          lead={selectedLead}
          onSendTemplate={(templateId, data) =>
            quickSendEmailMutation.mutate({ templateId, data })
          }
          onSendCustom={(data) => quickSendCustomEmailMutation.mutate(data)}
          isLoading={
            quickSendEmailMutation.isPending ||
            quickSendCustomEmailMutation.isPending
          }
        />

        {/* Assign Modal */}
        <AssignModal
          isOpen={showAssignModal}
          onClose={() => setShowAssignModal(false)}
          selectedLeads={selectedLeads}
          leads={leads}
          users={usersData || []}
          selectionMode={selectionMode}
          bulkSelectCount={bulkSelectCount}
          filters={{
            stage: activeTab !== "all" ? activeTab : "",
            status: statusFilter,
            search,
            activeTab,
          }}
          onSuccess={() => {
            setSelectedLeads([]);
            setSelectionMode("page");
            setShowAssignModal(false);
            queryClient.invalidateQueries(["leads"]);
            toast.success("Leads assigned successfully");
          }}
        />

        {/* Single Assign Modal */}
        <SingleAssignModal
          isOpen={showSingleAssignModal}
          onClose={() => {
            setShowSingleAssignModal(false);
            setSelectedLeadForAssign(null);
          }}
          lead={selectedLeadForAssign}
          users={usersData || []}
          onSuccess={() => {
            setShowSingleAssignModal(false);
            setSelectedLeadForAssign(null);
            queryClient.invalidateQueries(["leads"]);
            toast.success("Lead assigned successfully");
          }}
        />

        {/* Import Modal */}
        <Modal
          isOpen={showImportModal}
          onClose={() => {
            setShowImportModal(false);
            setImportData([]);
            setValidationResults(null);
            if (fileInputRef.current) {
              fileInputRef.current.value = "";
            }
          }}
          title="Import Leads"
          size="lg"
        >
          <div className="space-y-4 sm:space-y-6">
            {/* Country for numbers without a country code */}
            <div>
              <SearchableSelect
                label="Phone Number Country"
                searchPlaceholder="Search country or code (e.g. UAE, 971)"
                options={PHONE_COUNTRY_OPTIONS}
                value={importCountry}
                onChange={(e) => {
                  setImportCountry(e.target.value);
                  savePhoneCountry(e.target.value);
                  // Re-check already uploaded rows against the new country
                  if (importData.length > 0) {
                    applyImportValidation(importData, e.target.value);
                  }
                }}
              />
              <p className="text-xs text-gray-400 mt-1">
                Used for numbers without a country code. Numbers that start with + (or 00) are checked against their own country.
              </p>
            </div>

            {/* File Upload */}
            <div>
              <div className="flex items-center justify-between gap-2 mb-1.5 sm:mb-2">
                <label className="block text-xs sm:text-sm font-medium text-gray-700">
                  Upload CSV or Excel File
                </label>
                <button
                  type="button"
                  onClick={handleDownloadTemplate}
                  className="flex items-center gap-1 text-xs sm:text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
                  title="Download an Excel template with the correct columns and a sheet of valid values"
                >
                  <HiOutlineDownload className="w-4 h-4" />
                  Download Excel Template
                </button>
              </div>
              <div className="border-2 border-dashed border-gray-300 rounded-lg sm:rounded-xl p-4 sm:p-6 text-center hover:border-primary-500 transition-colors">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="csv-upload"
                />
                <label htmlFor="csv-upload" className="cursor-pointer">
                  <HiOutlineDocumentText className="w-8 h-8 sm:w-12 sm:h-12 text-gray-400 mx-auto mb-2" />
                  <p className="text-xs sm:text-sm text-gray-600">
                    Click to upload or drag and drop
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    CSV or Excel (.xlsx) file — name and phone required; alternatePhone, email,
                    stage, status, source, priority and notes are all optional (see format guide below)
                  </p>
                </label>
              </div>
            </div>

            {/* Validation Summary */}
            {validationResults && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg sm:rounded-xl p-3 sm:p-4">
                <div className="flex items-center gap-2 text-amber-700 font-medium mb-2 text-sm sm:text-base">
                  <HiOutlineExclamation className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span>Validation Summary</span>
                </div>
                <div className="text-xs sm:text-sm text-amber-600 space-y-1">
                  <p>
                    Total leads in file:{" "}
                    <strong>{validationResults.total}</strong>
                  </p>
                  <p className="text-green-600">
                    Valid leads: <strong>{validationResults.valid}</strong>
                  </p>
                  {validationResults.duplicateInFile > 0 && (
                    <p className="text-red-600">
                      Duplicates within file:{" "}
                      <strong>{validationResults.duplicateInFile}</strong>
                    </p>
                  )}
                  {validationResults.duplicates > 0 && (
                    <p className="text-red-600">
                      Already in system:{" "}
                      <strong>{validationResults.duplicates}</strong>
                    </p>
                  )}
                  <p className="text-amber-600">
                    Invalid leads: <strong>{validationResults.invalid}</strong>
                  </p>
                </div>
              </div>
            )}

            {/* Preview — Invalid / Valid tabs with inline + bulk edit */}
            {importData.length > 0 && (
              <ImportPreview
                key={importUploadId}
                rows={importData}
                onRowsChange={(rows) => applyImportValidation(rows)}
              />
            )}

            {/* Distribution Option */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg sm:rounded-xl p-3 sm:p-4">
              <label className="flex items-center gap-2 sm:gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={distributeEqually}
                  onChange={(e) => setDistributeEqually(e.target.checked)}
                  className="w-4 h-4 sm:w-5 sm:h-5 rounded border-blue-300 text-primary-600"
                />
                <div>
                  <div className="flex items-center gap-2 text-blue-800 font-medium text-sm sm:text-base">
                    <HiOutlineUsers className="w-4 h-4 sm:w-5 sm:h-5" />
                    Distribute equally to all telecallers (overrides Auto-Assign Rules)
                  </div>
                  <p className="text-xs sm:text-sm text-blue-600 mt-1">
                    Skips your organization's Auto-Assign Rules and splits leads evenly among{" "}
                    {usersData?.length || 0} telecallers instead.
                  </p>
                </div>
              </label>
              {!distributeEqually && (
                <p className="text-xs sm:text-sm text-blue-600 mt-2 pl-6 sm:pl-8">
                  Leads will follow your organization's Auto-Assign Rules (Leads → Auto-Assign
                  Rules), or stay unassigned if none match.
                </p>
              )}
            </div>

            {/* Products (Optional) - applied to all imported leads */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg sm:rounded-xl p-3 sm:p-4">
              <label className="block text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-1">
                <HiOutlineCube className="w-4 h-4" />
                Products (Optional)
              </label>
              {products.length === 0 ? (
                <p className="text-xs text-gray-500">No products available</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {products.map((product) => {
                    const selected = importProductIds.includes(product._id);
                    return (
                      <button
                        type="button"
                        key={product._id}
                        onClick={() =>
                          setImportProductIds((prev) =>
                            selected
                              ? prev.filter((id) => id !== product._id)
                              : [...prev, product._id]
                          )
                        }
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border-2 text-sm font-medium transition-all ${
                          selected
                            ? "bg-primary-50 border-primary-400 text-primary-700"
                            : "bg-white border-gray-200 text-gray-600 hover:border-gray-300"
                        }`}
                      >
                        <HiOutlineCube className="w-4 h-4" />
                        {product.name}
                      </button>
                    );
                  })}
                </div>
              )}
              <p className="text-xs text-gray-500 mt-1.5">
                Selected products will be applied to all imported leads
              </p>
            </div>

            {/* CSV Format Help — violet/indigo colour combination with a dark
                code-editor block for the sample data */}
            <div className="bg-indigo-50/70 border border-indigo-100 dark:bg-indigo-500/10 dark:border-indigo-500/25 rounded-lg sm:rounded-xl p-3 sm:p-4">
              <h4 className="font-semibold text-indigo-800 dark:text-indigo-300 mb-2 text-sm sm:text-base">
                File Format Example:
              </h4>
              {/* Sample data — theme-matched code block: light lavender card in
                  light mode, violet-navy "code editor" surface in dark mode
                  (token colours enforced by the html.dark .csv-sample rules in
                  index.css) */}
              <div className="csv-sample rounded-lg border border-indigo-200 bg-indigo-50/80 shadow-inner overflow-hidden dark:border-[#2a3158] dark:bg-[#121832] dark:shadow-none">
                <div className="flex items-center gap-1.5 px-3 pt-2.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-red-400/80" />
                  <span className="w-2.5 h-2.5 rounded-full bg-amber-400/80" />
                  <span className="w-2.5 h-2.5 rounded-full bg-green-400/80" />
                  <span className="csv-file ml-2 text-[10px] font-mono uppercase tracking-wider text-indigo-500 dark:text-indigo-300/70">
                    leads-import-template.xlsx — Sheet 1: Leads Template
                  </span>
                </div>
                <pre className="px-3 pb-3 pt-1.5 overflow-x-auto font-mono text-xs leading-relaxed">
                  <code>
                    <span className="csv-h text-indigo-700 dark:text-sky-300">
                      name,phone,alternatePhone,email,stage,status,source,priority,notes
                    </span>
                    {"\n"}
                    <span className="csv-name text-emerald-700 dark:text-emerald-300">John Doe,</span>
                    <span className="csv-phone text-slate-700 dark:text-slate-200">9876543210,</span>
                    <span className="csv-phone text-emerald-700 dark:text-emerald-300">9876543211,</span>
                    <span className="csv-email text-amber-700 dark:text-amber-200">john@email.com,</span>
                    <span className="csv-status text-slate-700 dark:text-slate-200">new_lead,new,Website,high,</span>
                    {"\n"}
                    <span className="csv-name text-emerald-700 dark:text-emerald-300">Jane Smith,</span>
                    <span className="csv-phone text-slate-700 dark:text-slate-200">9876543211,,</span>
                    <span className="csv-email text-amber-700 dark:text-amber-200">jane@email.com,</span>
                    <span className="csv-status text-slate-700 dark:text-slate-200">connected,warm,Referral,medium,</span>
                  </code>
                </pre>
              </div>
              <div className="mt-2 space-y-1">
                <p className="text-xs text-slate-600 dark:text-slate-400">
                  <strong className="text-indigo-700 dark:text-indigo-300">Tip:</strong> the downloaded template also has a
                  second sheet — "Valid Values" — listing every accepted Stage, Status, Priority and Source value.
                </p>
                <p className="text-xs text-slate-600 dark:text-slate-400">
                  <strong className="text-indigo-700 dark:text-indigo-300">Required:</strong> name, phone. Everything else
                  (alternatePhone, email, stage, status, source, priority, notes) is optional.
                </p>
                <p className="text-xs text-slate-600 dark:text-slate-400">
                  <strong className="text-indigo-700 dark:text-indigo-300">Phone:</strong> Validated per country. Numbers without a country code are checked
                  against the "Phone Number Country" selected above; others need the code (e.g. +971501234567, +14155551234, 9876543210)
                </p>
                <p className="text-xs text-slate-600 dark:text-slate-400">
                  <strong className="text-indigo-600 dark:text-indigo-300">Stage</strong> (pipeline stage — where the lead sits
                  in your funnel): new_lead, contact_attempted, not_connected, connected, qualified,
                  follow_up_required, meeting_scheduled, proposal_sent, negotiation, converted, lost, not_interested.
                  Leave blank to default to "New Lead". Your own custom stages also work if you've renamed any.
                </p>
                <p className="text-xs text-slate-600 dark:text-slate-400">
                  <strong className="text-indigo-600 dark:text-indigo-300">Status</strong> (call/contact disposition —
                  separate from stage): new, open, in_progress, follow_up, cold, warm, hot, converted, closed,
                  lost, not_connected. Leave blank to default to "new".
                </p>
                <p className="text-xs text-slate-600 dark:text-slate-400">
                  <strong className="text-indigo-600 dark:text-indigo-300">Priority:</strong> urgent, high, medium, low
                  (matches Leads → Auto-Assign Rules). Leave blank to default to "medium".
                </p>
                <p className="text-xs text-slate-600 dark:text-slate-400">
                  <strong className="text-indigo-600 dark:text-indigo-300">Source:</strong> any text describing where the
                  lead came from. To match your Auto-Assign Rules by source, use one of: Website, Referral, Cold Call,
                  LinkedIn, Email Campaign, Social Media, Advertisement, Walk-in, Phone Inquiry, Other.
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2 sm:gap-3 justify-end pt-3 sm:pt-4 border-t border-gray-100">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowImportModal(false);
                  setImportData([]);
                  setValidationResults(null);
                  if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                  }
                }}
              >
                Cancel
              </Button>
              {validationResults && validationResults.invalid > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleImport}
                  loading={importing}
                  disabled={importData.filter((l) => l.isValid).length === 0}
                >
                  Import Valid ({validationResults.valid})
                </Button>
              )}
              <Button
                variant="primary"
                size="sm"
                onClick={handleImport}
                loading={importing}
                disabled={importData.filter((l) => l.isValid).length === 0}
              >
                {validationResults && validationResults.invalid > 0
                  ? `Import ${validationResults.valid}`
                  : `Import ${importData.length}`}
              </Button>
            </div>
          </div>
        </Modal>

        {/* Export Modal */}
        <ExportLeadsModal
          isOpen={exportScope !== null}
          onClose={() => setExportScope(null)}
          initialScope={exportScope || "all"}
          currentFilters={{
            stage: activeTab,
            status: statusFilter,
            assignment: assignmentFilter,
            assignedTo: personFilter,
            search,
            sortBy,
            sortOrder,
          }}
          selection={{
            mode: selectionMode,
            ids: selectedLeads,
            count: bulkSelectCount,
            pageLeads: leads,
          }}
          stageOptions={stageOptions}
          users={usersData || []}
          products={products}
        />

        {/* Bulk Select Modal */}
        <BulkSelectModal
          isOpen={showBulkSelectModal}
          onClose={() => setShowBulkSelectModal(false)}
          totalLeads={filteredCountData || 0}
          currentFilters={{
            stage: activeTab !== "all" ? activeTab : "",
            status: statusFilter,
            search,
            activeTab,
          }}
          onConfirm={(count) => {
            setBulkSelectCount(count);
            setSelectionMode("count");
            setSelectedLeads([]);
            setShowBulkSelectModal(false);
          }}
        />

        {/* Bulk Stage Modal */}
        <BulkStageModal
          isOpen={showBulkStageModal}
          onClose={() => setShowBulkStageModal(false)}
          selectedLeads={selectedLeads}
          stages={stages}
          onConfirm={(stage) => {
            bulkUpdateStageMutation.mutate({ ids: selectedLeads, stage });
          }}
          isLoading={bulkUpdateStageMutation.isPending}
        />

        {/* Bulk Status Modal */}
        <BulkStatusModal
          isOpen={showBulkStatusModal}
          onClose={() => setShowBulkStatusModal(false)}
          selectedLeads={selectedLeads}
          onConfirm={(status) => {
            bulkUpdateStatusMutation.mutate({ ids: selectedLeads, status });
          }}
          isLoading={bulkUpdateStatusMutation.isPending}
        />

        {/* Delete Single Lead Confirm */}
        <ConfirmDialog
          isOpen={showDeleteConfirm}
          onClose={() => { setShowDeleteConfirm(false); setDeleteTarget(null); }}
          onConfirm={confirmDelete}
          title="Delete Lead"
          message="Are you sure you want to delete this lead? This action cannot be undone and all associated data will be permanently removed."
          confirmText="Delete Lead"
          cancelText="Cancel"
          variant="danger"
          icon={HiOutlineTrash}
          loading={deleteMutation.isPending}
        />

        {/* Delete Bulk Leads Confirm */}
        <ConfirmDialog
          isOpen={showBulkDeleteConfirm}
          onClose={() => setShowBulkDeleteConfirm(false)}
          onConfirm={confirmBulkDelete}
          title="Delete Selected Leads"
          message={`Are you sure you want to delete ${selectedLeads.length} lead${selectedLeads.length > 1 ? "s" : ""}? This action cannot be undone and all associated data will be permanently removed.`}
          confirmText={`Delete ${selectedLeads.length} Lead${selectedLeads.length > 1 ? "s" : ""}`}
          cancelText="Cancel"
          variant="danger"
          icon={HiOutlineTrash}
          loading={bulkDeleteMutation.isPending}
        />
      </div>

      {/* Quick Actions Dropdown Menu — rendered via portal to escape overflow clipping */}
      {openMenuLeadId && selectedLead && createPortal(
        <div
          data-quick-menu
          className="fixed z-[9999] w-52 bg-white rounded-xl shadow-xl border border-gray-200 py-1.5 animate-scale-in"
          style={{
            bottom: `${menuPosition.bottom}px`,
            left: `${menuPosition.left}px`,
          }}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <button
            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-700 flex items-center gap-2.5 transition-colors"
            onClick={() => {
              setOpenMenuLeadId(null);
              setShowQuickStageModal(true);
            }}
          >
            <HiOutlineTag className="w-4 h-4 text-gray-400" />
            Update Stage
          </button>
          <button
            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-700 flex items-center gap-2.5 transition-colors"
            onClick={() => {
              setOpenMenuLeadId(null);
              setShowQuickStatusModal(true);
            }}
          >
            <HiOutlineClipboardCheck className="w-4 h-4 text-gray-400" />
            Update Status
          </button>
          <button
            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-700 flex items-center gap-2.5 transition-colors"
            onClick={() => {
              setOpenMenuLeadId(null);
              setShowQuickNoteModal(true);
            }}
          >
            <HiOutlineChatAlt className="w-4 h-4 text-gray-400" />
            Add Note
          </button>
          <button
            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-700 flex items-center gap-2.5 transition-colors"
            onClick={() => {
              setOpenMenuLeadId(null);
              setShowQuickFollowUpModal(true);
            }}
          >
            <HiOutlineCalendar className="w-4 h-4 text-gray-400" />
            Schedule Follow-up
          </button>
          <div className="border-t border-gray-100 my-1" />
          {selectedLead.email && (
            <button
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-700 flex items-center gap-2.5 transition-colors"
              onClick={() => {
                setOpenMenuLeadId(null);
                setShowQuickEmailModal(true);
              }}
            >
              <HiOutlineMail className="w-4 h-4 text-gray-400" />
              Send Email
            </button>
          )}
          <button
            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-700 flex items-center gap-2.5 transition-colors"
            onClick={() => {
              setOpenMenuLeadId(null);
              window.open(`tel:${selectedLead.phone}`, '_self');
            }}
          >
            <HiOutlinePhone className="w-4 h-4 text-gray-400" />
            Call
          </button>
        </div>,
        document.body
      )}
    </PageLayout>
  );
};

// Assign Modal Component
const AssignModal = ({
  isOpen,
  onClose,
  selectedLeads,
  leads,
  users,
  onSuccess,
  selectionMode,
  bulkSelectCount,
  filters,
}) => {
  const [selectedUser, setSelectedUser] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Calculate assignment stats for selected leads
  const getAssignmentStats = () => {
    if (selectionMode === "count" || !leads) {
      return {
        unassigned: 0,
        assigned: 0,
        assignedTo: null,
        assignedNames: [],
      };
    }

    const selectedLeadsData = leads.filter((l) =>
      selectedLeads.includes(l._id),
    );
    const unassigned = selectedLeadsData.filter((l) => !l.assignedTo).length;
    const assigned = selectedLeadsData.filter((l) => l.assignedTo).length;

    // Get unique assigned users
    const assignedUsers = {};
    selectedLeadsData.forEach((l) => {
      if (l.assignedTo?._id) {
        assignedUsers[l.assignedTo._id] =
          `${l.assignedTo.firstName} ${l.assignedTo.lastName}`;
      }
    });
    const assignedNames = Object.values(assignedUsers);

    // If all assigned to same person
    const allAssignedToSame =
      assigned > 0 && Object.keys(assignedUsers).length === 1;
    const assignedTo = allAssignedToSame ? Object.keys(assignedUsers)[0] : null;

    return { unassigned, assigned, assignedTo, assignedNames };
  };

  const stats = getAssignmentStats();

  const handleAssign = async () => {
    if (!selectedUser) {
      toast.error("Select a user to assign");
      return;
    }

    setIsSubmitting(true);
    try {
      if (selectionMode === "count") {
        // Assign by count with filters
        await leadsAPI.assignByFilters({
          assignedTo: selectedUser,
          count: bulkSelectCount,
          stage: filters?.stage,
          status: filters?.status,
          search: filters?.search,
          activeTab: filters?.activeTab,
        });
      } else {
        // Assign by specific lead IDs
        await leadsAPI.bulkAssign({
          leadIds: selectedLeads,
          assignedTo: selectedUser,
        });
      }
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to assign leads");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Pre-select the current assignee if all leads are assigned to same person
  React.useEffect(() => {
    if (stats.assignedTo) {
      setSelectedUser(stats.assignedTo);
    } else {
      setSelectedUser("");
    }
  }, [stats.assignedTo]);

  if (!isOpen) return null;

  const displayCount =
    selectionMode === "count" ? bulkSelectCount : selectedLeads.length;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Assign Leads" size="sm">
      <div className="space-y-3 sm:space-y-4">
        <p className="text-gray-600 text-sm">
          Assign <span className="font-semibold">{displayCount}</span> leads to
          a telecaller
        </p>

        {/* Show assignment status */}
        {selectionMode !== "count" && (
          <div className="space-y-2">
            {stats.unassigned > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5 sm:p-3">
                <p className="text-xs sm:text-sm text-blue-700">
                  <span className="font-medium">{stats.unassigned}</span>{" "}
                  unassigned leads will be assigned
                </p>
              </div>
            )}
            {stats.assigned > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 sm:p-3">
                <p className="text-xs sm:text-sm text-amber-700">
                  <span className="font-medium">{stats.assigned}</span> leads
                  are already assigned
                  {stats.assignedNames.length > 0 && (
                    <span className="block mt-1">
                      Currently assigned to:{" "}
                      <strong>{stats.assignedNames.join(", ")}</strong>
                    </span>
                  )}
                </p>
                <p className="text-xs text-amber-600 mt-1">
                  These leads will be reassigned to the telecaller you select
                  below.
                </p>
              </div>
            )}
          </div>
        )}

        {selectionMode === "count" && (
          <p className="text-xs text-gray-500">
            Only unassigned leads matching your filters will be assigned.
          </p>
        )}

        <Select
          label="Select Telecaller"
          options={users.map((u) => ({
            value: u._id,
            label: `${u.firstName} ${u.lastName}`,
          }))}
          placeholder="Choose a telecaller"
          value={selectedUser}
          onChange={(e) => setSelectedUser(e.target.value)}
        />
        <div className="flex gap-2 sm:gap-3 justify-end pt-3 sm:pt-4">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleAssign}
            loading={isSubmitting}
          >
            {stats.assigned > 0 ? "Reassign" : "Assign"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// Single Assign Modal Component
const SingleAssignModal = ({ isOpen, onClose, lead, users, onSuccess }) => {
  const [selectedUser, setSelectedUser] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  React.useEffect(() => {
    if (lead?.assignedTo?._id) {
      setSelectedUser(lead.assignedTo._id);
    } else {
      setSelectedUser("");
    }
  }, [lead]);

  const handleAssign = async () => {
    if (!selectedUser) {
      toast.error("Select a user to assign");
      return;
    }

    setIsSubmitting(true);
    try {
      await leadsAPI.update(lead._id, { assignedTo: selectedUser });
      onSuccess();
    } catch (error) {
      toast.error("Failed to assign lead");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen || !lead) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Assign Lead" size="sm">
      <div className="space-y-3 sm:space-y-4">
        <div className="bg-gray-50 rounded-lg sm:rounded-xl p-2.5 sm:p-3">
          <p className="text-xs text-gray-500">Lead</p>
          <p className="font-medium text-gray-900">{lead.name}</p>
          <p className="text-xs sm:text-sm text-gray-500">{lead.phone}</p>
        </div>
        {lead.assignedTo && (
          <p className="text-xs sm:text-sm text-amber-600">
            Currently assigned to:{" "}
            <strong>
              {lead.assignedTo.firstName} {lead.assignedTo.lastName}
            </strong>
          </p>
        )}
        <Select
          label="Select Telecaller"
          options={users.map((u) => ({
            value: u._id,
            label: `${u.firstName} ${u.lastName}`,
          }))}
          placeholder="Choose a telecaller"
          value={selectedUser}
          onChange={(e) => setSelectedUser(e.target.value)}
        />
        <div className="flex gap-2 sm:gap-3 justify-end pt-3 sm:pt-4">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleAssign}
            loading={isSubmitting}
          >
            {lead.assignedTo ? "Reassign" : "Assign"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// Bulk Select Modal Component
const BulkSelectModal = ({
  isOpen,
  onClose,
  totalLeads,
  currentFilters,
  onConfirm,
}) => {
  const [selectCount, setSelectCount] = useState(50);
  const [customCount, setCustomCount] = useState("");

  const presetOptions = [
    { value: 50, label: "50 leads" },
    { value: 100, label: "100 leads" },
    { value: 200, label: "200 leads" },
    { value: 500, label: "500 leads" },
    { value: -1, label: "All leads" },
    { value: 0, label: "Custom count" },
  ];

  const handleConfirm = () => {
    const finalCount =
      selectCount === 0
        ? parseInt(customCount) || 50
        : selectCount === -1
          ? totalLeads
          : selectCount;

    if (finalCount <= 0) {
      toast.error("Please enter a valid count");
      return;
    }

    if (finalCount > totalLeads) {
      toast.error(`Only ${totalLeads} leads available with current filters`);
      return;
    }

    onConfirm(finalCount);
  };

  if (!isOpen) return null;

  const getFilterDescription = () => {
    const filters = [];
    if (currentFilters?.stage) filters.push(`Stage: ${currentFilters.stage}`);
    if (currentFilters?.status)
      filters.push(`Status: ${currentFilters.status}`);
    if (currentFilters?.search)
      filters.push(`Search: "${currentFilters.search}"`);
    if (currentFilters?.activeTab && currentFilters.activeTab !== "all") {
      filters.push(`Tab: ${currentFilters.activeTab}`);
    }
    return filters.length > 0 ? filters.join(", ") : "No filters applied";
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Bulk Select Leads"
      size="sm"
    >
      <div className="space-y-3 sm:space-y-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg sm:rounded-xl p-3 sm:p-4">
          <div className="flex items-center gap-2 text-blue-700 font-medium mb-1.5 sm:mb-2 text-sm sm:text-base">
            <HiOutlineUsers className="w-4 h-4 sm:w-5 sm:h-5" />
            <span>{totalLeads.toLocaleString()} leads available</span>
          </div>
          <p className="text-xs sm:text-sm text-blue-600">
            {getFilterDescription()}
          </p>
        </div>

        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
            Select how many leads to assign
          </label>
          <Select
            options={presetOptions.map((o) => ({
              value: o.value,
              label: o.label,
            }))}
            value={selectCount}
            onChange={(e) => setSelectCount(parseInt(e.target.value))}
          />
        </div>

        {selectCount === 0 && (
          <Input
            label="Enter custom count"
            type="number"
            value={customCount}
            onChange={(e) => setCustomCount(e.target.value)}
            placeholder={`Max ${totalLeads}`}
            min={1}
            max={totalLeads}
          />
        )}

        <div className="bg-gray-50 rounded-lg sm:rounded-xl p-2.5 sm:p-3">
          <p className="text-xs sm:text-sm text-gray-600">
            <strong>Selected:</strong>{" "}
            {selectCount === 0
              ? customCount || 0
              : selectCount === -1
                ? totalLeads
                : selectCount}{" "}
            leads
          </p>
        </div>

        <div className="flex gap-2 sm:gap-3 justify-end pt-3 sm:pt-4">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={handleConfirm}>
            Select
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// Bulk Stage Modal Component
const BulkStageModal = ({ isOpen, onClose, selectedLeads, stages, onConfirm, isLoading }) => {
  const [selectedStage, setSelectedStage] = useState("");

  const handleConfirm = () => {
    if (!selectedStage) {
      toast.error("Please select a stage");
      return;
    }
    onConfirm(selectedStage);
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Change Stage" size="sm">
      <div className="space-y-3 sm:space-y-4">
        <p className="text-gray-600 text-sm">
          Change stage for <span className="font-semibold">{selectedLeads.length}</span> selected leads
        </p>

        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
            Select New Stage
          </label>
          <Select
            options={[
              { value: "", label: "Choose a stage" },
              ...stages.map((stage) => ({
                value: stage.key,
                label: stage.name,
              })),
            ]}
            value={selectedStage}
            onChange={(e) => setSelectedStage(e.target.value)}
          />
        </div>

        <div className="flex gap-2 sm:gap-3 justify-end pt-3 sm:pt-4">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleConfirm}
            loading={isLoading}
            disabled={!selectedStage}
          >
            Update Stage
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// Bulk Status Modal Component
const BulkStatusModal = ({ isOpen, onClose, selectedLeads, onConfirm, isLoading }) => {
  const [selectedStatus, setSelectedStatus] = useState("");

  const statusOptions = [
    { value: "", label: "Choose a status" },
    { value: "new", label: "New" },
    { value: "open", label: "Open" },
    { value: "in_progress", label: "In Progress" },
    { value: "follow_up", label: "Follow Up" },
    { value: "cold", label: "Cold" },
    { value: "warm", label: "Warm" },
    { value: "hot", label: "Hot" },
    { value: "converted", label: "Converted" },
  ];

  const handleConfirm = () => {
    if (!selectedStatus) {
      toast.error("Please select a status");
      return;
    }
    onConfirm(selectedStatus);
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Change Status" size="sm">
      <div className="space-y-3 sm:space-y-4">
        <p className="text-gray-600 text-sm">
          Change status for <span className="font-semibold">{selectedLeads.length}</span> selected leads
        </p>

        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
            Select New Status
          </label>
          <Select
            options={statusOptions}
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
          />
        </div>

        <div className="flex gap-2 sm:gap-3 justify-end pt-3 sm:pt-4">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleConfirm}
            loading={isLoading}
            disabled={!selectedStatus}
          >
            Update Status
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// Commented out - Bulk Priority Modal not needed
// const BulkPriorityModal = ({ isOpen, onClose, selectedLeads, onConfirm, isLoading }) => {
//   const [selectedPriority, setSelectedPriority] = useState("");
//
//   const handleConfirm = () => {
//     if (!selectedPriority) {
//       toast.error("Please select a priority");
//       return;
//     }
//     onConfirm(selectedPriority);
//   };
//
//   if (!isOpen) return null;
//
//   return (
//     <Modal isOpen={isOpen} onClose={onClose} title="Change Priority" size="sm">
//       <div className="space-y-3 sm:space-y-4">
//         <p className="text-gray-600 text-sm">
//           Change priority for <span className="font-semibold">{selectedLeads.length}</span> selected leads
//         </p>
//
//         <div>
//           <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
//             Select New Priority
//           </label>
//           <Select
//             options={[
//               { value: "", label: "Choose a priority" },
//               ...priorityOptions,
//             ]}
//             value={selectedPriority}
//             onChange={(e) => setSelectedPriority(e.target.value)}
//           />
//         </div>
//
//         <div className="flex gap-2 sm:gap-3 justify-end pt-3 sm:pt-4">
//           <Button variant="ghost" size="sm" onClick={onClose}>
//             Cancel
//           </Button>
//           <Button
//             variant="primary"
//             size="sm"
//             onClick={handleConfirm}
//             loading={isLoading}
//             disabled={!selectedPriority}
//           >
//             Update Priority
//           </Button>
//         </div>
//       </div>
//     </Modal>
//   );
// };

export default LeadsList;
